/**
 * Online validation constants and tuning shared by client and server.
 *
 * Vehicle limits are DERIVED from the single-player roster (`src/vehicles/specs.ts`) so the server
 * validates against exactly the handling the client simulates. World bounds come from the authored
 * Low Borough data. Both imports are type-light pure data (no DOM, no three.js).
 *
 * See docs/ONLINE.md §3 (tick/bandwidth maths) and §5 (movement validation).
 */
import { VEHICLE_SPECS } from '../src/vehicles/specs';
import { BOUNDS, PLAYER_SPAWN, POIS } from '../src/world/lowBorough/data';

// ----------------------------------------------------------------------------- rates & capacity

/** Server simulation + snapshot rate. The client keeps its own 60 Hz loop and interpolates. */
export const SERVER_TICK_HZ = 20;
export const SERVER_TICK_MS = 1000 / SERVER_TICK_HZ;
/** Client movement upload rate (one `mv` per server tick). */
export const CLIENT_SEND_HZ = 20;
/** Remote entities are rendered this far in the past so there are always two snapshots to blend. */
export const INTERP_DELAY_MS = 100;

/** Shard selection prefers shards below the soft cap (density), never exceeds the hard cap. */
export const SHARD_SOFT_CAP = 24;
export const SHARD_HARD_CAP = 32;

/** Interest management: entities enter a client's view inside ENTER, leave beyond EXIT (hysteresis). */
export const INTEREST_ENTER_RADIUS = 180;
export const INTEREST_EXIT_RADIUS = 200;

// ----------------------------------------------------------------------------- world

export interface Bounds {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

/** Playable area (Low Borough). Positions outside (plus a small margin) are always rejected. */
export const WORLD_BOUNDS: Bounds = { minX: BOUNDS.minX, minZ: BOUNDS.minZ, maxX: BOUNDS.maxX, maxZ: BOUNDS.maxZ };
export const WORLD_BOUNDS_MARGIN = 2;

// ----------------------------------------------------------------------------- movement validation

/** Mirrors `RUN_SPEED` in src/player/playerSystem.ts (checked by shared/limits.client.test.ts). */
export const FOOT_MAX_SPEED = 6.6;
/** Allowed overshoot over the nominal top speed (network jitter, collision shoves). */
export const SPEED_TOLERANCE = 1.25;
/**
 * Movement budget: a client may "save up" this many seconds of travel at top speed. Absorbs bunched
 * packets after a lag spike without allowing sustained speed hacks.
 */
export const MOVE_BURST_SECONDS = 1.0;
/** Minimum burst allowance in metres (being flung by a car on foot, bumper shoves). */
export const MOVE_BURST_MIN_METRES = 12;
/** Any single step longer than this is a teleport and always rejected. */
export const TELEPORT_DISTANCE = 40;
/** A position this deep inside a building collider is rejected (physics allows slight penetration). */
export const COLLIDER_PENETRATION_SLACK = 0.6;
/** Rejections within the window before the session is flagged and kicked. */
export const MOVE_VIOLATION_KICK = 25;
export const MOVE_VIOLATION_WINDOW_MS = 10_000;

/**
 * Acceleration sanity (vehicles): between two consecutive accepted proposals the reported speed may
 * rise by at most (spec accel × ACCEL_TOLERANCE + ACCEL_SHOVE) × elapsed + ACCEL_SLACK. The shove term
 * covers being rammed; the slack covers quantisation and packet bunching.
 */
export const ACCEL_TOLERANCE = 1.5;
export const ACCEL_SHOVE = 30;
export const ACCEL_SLACK = 3;
/** On foot the run speed is reached almost instantly, so the per-step slack is the whole run speed. */
export const FOOT_ACCEL_SLACK = 7;

export const ENTER_VEHICLE_RANGE = 5;
export const EXIT_VEHICLE_RANGE = 6;
/** Max distance between the player and an ambient car they claim to have stolen. */
export const CLAIM_VEHICLE_RANGE = 6;
/** Time to force a locked car (stand within ENTER range). */
export const BREAK_IN_MS = 2500;
/** Most damage one `vdmg` report may apply, and per vehicle per second. */
export const VDMG_MAX_PER_REPORT = 60;
export const VDMG_MAX_PER_SECOND = 150;

// ----------------------------------------------------------------------------- collisions (server-authoritative)

/** A `vhit` the driver hasn't acknowledged (`mv.k`) by now is forced with a correction (reason `hit`). */
export const HIT_ACK_MS = 1500;
/**
 * A car at 0 health burns this long before it blows (s), and its driver bails out when this much of the
 * fuse is left (s): the client's FIRE_FUSE (src/vehicles/vehicleSystem.ts, pinned equal by a test) and its
 * bail-out (src/player/playerSystem.ts). The server lets a wreck be driven for that long (WRECK_COAST_MS).
 */
export const VEHICLE_FIRE_FUSE_S = 4.5;
/** A police car burns longer before it goes up (the mayhem pass): a bigger blaze at a roadblock, a chain when they're close. */
export const POLICE_FIRE_FUSE_S = 9;
/** A wreck going up damages cars within this radius (m); a badly hurt car within CHAIN_IGNITE_RADIUS catches fire too. */
export const EXPLOSION_RADIUS_M = 9;
/**
 * People on foot within this radius (m) of a wreck going up are knocked flat (src/fx/explosionFx.ts offline; the
 * server's foot officers online, server/police/force.ts); closer than BLAST_KILL_RADIUS_M a pedestrian does not get up.
 */
export const BLAST_KNOCK_RADIUS_M = 10;
export const BLAST_KILL_RADIUS_M = 4;
export const CHAIN_IGNITE_RADIUS = 7;
export const CHAIN_IGNITE_HEALTH = 0.5;
export const BURNING_CAR_BAIL_S = 1.2;
/** A server placement (`corr.n`) not acknowledged in `mv.c` after this long (ms) is sent again (no strike). */
export const PLACE_ACK_MS = 1000;
/**
 * A knock the server cannot see: the client's own ambient traffic (per-client scenery) ran into the car
 * it drives. Once per UNSEEN_KNOCK_MS a car's speed may rise by up to this much (m/s) more than its spec
 * acceleration allows: the heaviest ambient vehicle (a lorry) at a brisk street speed into a standing car.
 * Still capped by the top-speed rule and the movement budget; a launch from 5 to 30 m/s is still `accel`.
 */
export const UNSEEN_KNOCK_DV = 12;
export const UNSEEN_KNOCK_MS = 1500;
/** The same two cars can't collide again for this long (one crash = one impulse). */
export const COLLISION_PAIR_COOLDOWN_MS = 400;
/** One car can run the same player over at most once per this long. */
export const RUN_OVER_COOLDOWN_MS = 1000;
/** A car moving at least this fast (m/s, server-observed) knocks a player on foot down. */
export const RUN_OVER_MIN_SPEED = 4;
/** How long a run-over player stays down, and how far from where they fell they may be meanwhile. */
export const KNOCKDOWN_MS = 1100;
export const KNOCKDOWN_RADIUS = 2.5;
/** Deceleration of a car nobody drives after a shove (handbrake on), and of a driven car while the server owns it. */
export const SHOVED_DECEL = 7;
export const COASTING_DECEL = 3;
/** Reported speed above what the car was seen to move (m/s) is ignored by collisions. */
export const OBSERVED_SPEED_SLACK = 3;
/**
 * A server-run police unit weighs this many times its spec in a crash: on the client its proxy (heavy
 * but finite, so a PIT is a shove and not a launch: src/net/netSystem.ts) and in the server's own
 * resolution (Shard.stepCollisions), so both give a rammed driver the same bounce.
 */
export const UNIT_PROXY_MASS = 4;

export interface VehicleLimits {
  specId: string;
  /** Forward top speed, m/s. */
  maxSpeed: number;
  maxReverse: number;
  accel: number;
  halfWidth: number;
  halfLength: number;
  maxHealth: number;
  seats: number;
}

const SEATS_BY_CLASS: Record<string, number> = { compact: 2, saloon: 4, van: 2, sports: 2, police: 2, cab: 4, lorry: 2 };
/** Police vehicle spec ids (the saloon and the van). */
export const POLICE_SPEC_IDS: readonly string[] = ['police', 'policevan'];

/** Per-spec validation limits, derived from the single-player roster. Index order is stable (wire ids). */
export const VEHICLE_SPEC_IDS: readonly string[] = Object.keys(VEHICLE_SPECS);
export const VEHICLE_LIMITS: Readonly<Record<string, VehicleLimits>> = Object.fromEntries(
  VEHICLE_SPEC_IDS.map((id) => {
    const s = VEHICLE_SPECS[id]!;
    const limits: VehicleLimits = {
      specId: id,
      maxSpeed: s.maxSpeed,
      maxReverse: s.maxReverse,
      accel: s.accel,
      halfWidth: s.width / 2,
      halfLength: s.length / 2,
      maxHealth: s.maxHealth,
      seats: SEATS_BY_CLASS[s.class] ?? 2,
    };
    return [id, limits];
  }),
);

export function vehicleSpecIndex(specId: string): number {
  return VEHICLE_SPEC_IDS.indexOf(specId);
}

// ----------------------------------------------------------------------------- sessions & presence

/** Disconnect grace for a character with nothing at stake (stays in-world, then saved and removed). */
export const GRACE_MS_BASE = 15_000;
/** Disconnect grace while wanted / carrying goods / in a stolen car / in a job: logging out is never the safe escape. */
export const GRACE_MS_RISKY = 90_000;
/** Freshly (re)spawned players cannot be harmed by players (cancelled early by their own aggression). */
export const SPAWN_PROTECTION_MS = 10_000;
/** After attacking someone a player is an "aggressor": no safe-zone protection for this long. */
export const AGGRESSOR_MS = 30_000;

export interface SafeZone {
  id: string;
  x: number;
  z: number;
  radius: number;
}

const poi = (id: string): { x: number; z: number } => {
  const p = POIS.find((q) => q.id === id);
  if (!p) throw new Error(`safe zone POI ${id} missing from the layout`);
  return p.pos;
};

/**
 * No-PvP zones around the places players (re)appear: the spawn point, the safehouse and the police
 * station forecourt (respawn after an arrest). Players inside can't be harmed by players unless
 * they are themselves aggressors.
 */
export const SAFE_ZONES: readonly SafeZone[] = [
  { id: 'spawn', ...PLAYER_SPAWN.pos, radius: 20 },
  { id: 'safehouse', ...poi('safehouse'), radius: 12 },
  { id: 'police-station', ...poi('police-station'), radius: 15 },
];

export function safeZoneAt(x: number, z: number): SafeZone | null {
  for (const zone of SAFE_ZONES) if (Math.hypot(x - zone.x, z - zone.z) <= zone.radius) return zone;
  return null;
}
/** The client must send `hello` within this window after the socket opens. */
export const HELLO_TIMEOUT_MS = 5_000;
/** WebSocket ping cadence; a connection that misses ~2.5 pings is treated as lost. */
export const HEARTBEAT_MS = 10_000;
export const HEARTBEAT_TIMEOUT_MS = 25_000;
/** Characters' world positions are flushed to the DB this often (and on leave/shutdown). */
export const AUTOSAVE_MS = 10_000;

// ----------------------------------------------------------------------------- transport

/** Hard cap on one inbound frame. Bigger frames close the socket (1009). */
export const MAX_FRAME_BYTES = 16 * 1024;
/** Per-connection inbound budget (all message types). */
export const MSG_RATE_PER_SECOND = 60;
export const MSG_RATE_BURST = 120;

// ----------------------------------------------------------------------------- accounts

export const USERNAME_RE = /^[A-Za-z0-9_]{3,20}$/;
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

/** Cash a brand-new character starts with (minted through the ledger, reason `starter-grant`). */
export const STARTING_CASH = 500;
